<loading-overlay [isLoading]="isLoading() > 0" />

@if (isPosted() && opening(); as posted) {
  <p-panel [header]="'FundOpening_Posted_Title' | translateWF: 'Opening'">
    <p-card>
      <div class="grid grid-cols-12 gap-4">
        <div class="form-box col-span-12 md:col-span-6">
          <label>{{ 'FundOpening_Number' | translateWF: 'Movement' }}</label>
          <span class="font-mono">{{ posted.number }}</span>
        </div>
        <div class="form-box col-span-12 md:col-span-6">
          <label>{{ fieldLabels.movementDate | translateWF: 'Date' }}</label>
          <span>{{ posted.movementDate | date: 'dd/MM/yyyy' }}</span>
        </div>
        <div class="form-box col-span-12 md:col-span-6">
          <label>{{ fieldLabels.amount | translateWF: 'Amount' }}</label>
          <span>{{ symbol() }} {{ posted.amount | number: '1.2-2' }}</span>
        </div>
        <div class="form-box col-span-12 md:col-span-6">
          <label>{{ 'FundOpening_Charges' | translateWF: 'Bank charges' }}</label>
          <span>{{ symbol() }} {{ posted.transferFee + posted.securityTax | number: '1.2-2' }}</span>
        </div>
        <div class="form-box col-span-12">
          <label>{{ fieldLabels.bankAccount | translateWF: 'Bank account' }}</label>
          <span>{{ postedBankLabel() }}</span>
        </div>
        <div class="form-box col-span-12 md:col-span-6">
          <label>{{ 'FundOpening_JournalEntry' | translateWF: 'Journal entry' }}</label>
          <span class="font-mono">{{ posted.journalEntryNumber }}</span>
        </div>
        <div class="form-box col-span-12 md:col-span-6">
          <label>{{ 'FundOpening_BankMovement' | translateWF: 'Bank movement' }}</label>
          <span class="font-mono">{{ posted.bankTransactionNumber }}</span>
        </div>
        @if (posted.bankingReference) {
          <div class="form-box col-span-12">
            <label>{{ fieldLabels.bankingReference | translateWF: 'Bank transaction reference' }}</label>
            <span>{{ posted.bankingReference }}</span>
          </div>
        }
      </div>
    </p-card>
  </p-panel>
} @else {
  <p-panel [header]="'FundOpening_Title' | translateWF: 'Fund opening'">
    <p-card>
      <div class="flex flex-col gap-5">
        <div class="flex items-center gap-2 p-3 rounded bg-surface-100 text-surface-600 text-sm">
          <i class="pi pi-info-circle" aria-hidden="true"></i>
          @if (canFund()) {
            <span>{{
              'FundOpening_Hint' | translateWF: 'Send the opening cash from the bank. Posting books Dr fund / Cr bank, records the bank movement and makes the fund Active.'
            }}</span>
          } @else {
            <span>{{ 'FundOpening_Hint_ReadOnly' | translateWF: 'Waiting for the treasurer to post the opening movement.' }}</span>
          }
        </div>

        <form [formGroup]="form" class="grid grid-cols-12 gap-5">
          <div class="form-box col-span-12 md:col-span-6">
            <label for="opening-amount">{{ fieldLabels.amount | translateWF: 'Amount' }} *</label>
            <p-inputGroup>
              <p-inputnumber
                inputId="opening-amount"
                formControlName="amount"
                mode="decimal"
                [minFractionDigits]="2"
                [maxFractionDigits]="2"
                [min]="0"
                [max]="fund().maxRevolvingBalance"
                styleClass="w-full"
                inputStyleClass="w-full"
                [ngClass]="{ 'ng-invalid ng-dirty': form.controls.amount.invalid && form.controls.amount.touched }"
              />
              <p-inputGroupAddon>{{ currencyLabel() || '—' }}</p-inputGroupAddon>
            </p-inputGroup>
            <small class="text-surface-500">
              {{ 'FundOpening_Amount_Hint' | translateWF: 'Defaults to the fund’s maximum; it cannot exceed it.' }}
              {{ symbol() }} {{ fund().maxRevolvingBalance | number: '1.2-2' }}
            </small>
          </div>
          <div class="form-box col-span-12 md:col-span-6">
            <label for="opening-date">{{ fieldLabels.movementDate | translateWF: 'Date' }} *</label>
            <p-datepicker
              inputId="opening-date"
              formControlName="movementDate"
              dateFormat="dd/mm/yy"
              [showIcon]="true"
              appendTo="body"
              styleClass="w-full"
              [ngClass]="{ 'ng-invalid ng-dirty': form.controls.movementDate.invalid && form.controls.movementDate.touched }"
            />
          </div>
        </form>

        <app-bank-charges-section
          [title]="'FundOpening_Section_Bank' | translateWF: 'Bank, reference, charges & documents'"
          [bankAccountControl]="form.controls.bankAccount"
          [referenceControl]="form.controls.bankingReference"
          [bankingFeeControl]="form.controls.bankingFee"
          [securityFeeControl]="form.controls.securityFee"
          [bankAccountOptions]="bankOptions()"
          [noBankHint]="
            'FundOpening_NoBank_Hint'
              | translateWF: 'No active bank account in this currency allows Petty Cash. Accounting enables it on the bank’s GL account (posting module Caja Chica).'
          "
          [currencyLabel]="currencyLabel()"
          [attachments]="opening()?.attachments ?? []"
          [canAttach]="isEditable() && !!opening()"
          [bankingFeeHint]="'ApAdvance_BankingFee_Hint' | translateWF: 'Wire / transfer charge — added to the total paid.'"
          [securityFeeHint]="'ApAdvance_SecurityFee_Hint' | translateWF: 'Insurance / handling — added to the total paid.'"
          (fileSelected)="onFile($event)"
          (attachmentOpened)="openAttachment($event)"
        />

        <div class="form-box">
          <label for="opening-note">{{ fieldLabels.note | translateWF: 'Note' }}</label>
          <textarea id="opening-note" pTextarea class="w-full" rows="2" [formControl]="form.controls.note" maxlength="500"></textarea>
        </div>

        <div class="flex flex-wrap items-center justify-between gap-3">
          <span class="font-semibold"> {{ 'FundOpening_CashOut' | translateWF: 'Total leaving the bank' }}: {{ symbol() }} {{ totalCashOut() | number: '1.2-2' }} </span>
          @if (isEditable()) {
            <div class="flex gap-2">
              @if (opening()) {
                <p-button [label]="'Delete' | translateWF: 'Delete'" icon="pi pi-trash" severity="danger" variant="outlined" (onClick)="confirmDelete()" />
              }
              <p-button [label]="'FundOpening_SaveDraft' | translateWF: 'Save draft'" icon="pi pi-save" variant="outlined" (onClick)="save()" />
              <p-button [label]="'FundOpening_Post' | translateWF: 'Post'" icon="pi pi-check" (onClick)="confirmPost()" />
            </div>
          }
        </div>
      </div>
    </p-card>
  </p-panel>
}
